import React, { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  CircularProgress,
  colors,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import * as Yup from "yup";

import ExpandedContent from "../../icip_admin_screen/components/ExpandedContent";
import SettingsItem from "../../icip_admin_screen/components/SettingsItem";
import { InputField } from "../../../components/input-field/InputField";
import { OutlineButton } from "../../icip_admin_screen/components/Buttons";
import { InputButton } from "../../../components/input-button/InputButton";
import { FormikHelpers, useFormik } from "formik";
import ICIPAdminService from "../../../services/ICIPAdminService";

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    wrapper: {
      paddingLeft: "8px",
      "& h4": {
        fontWeight: theme.typography.fontWeightBold,
      },
      "& input": {
        fontSize: 14,
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    error: {
      fontSize: 13,
      color: colors.red[700],
      textAlign: "left",
    },
    loader: {
      marginTop: 8,
    },
    showPasswordLink: {
      display: "inline-block",
      marginTop: "6px",
      fontSize: 13,
      fontWeight: theme.typography.fontWeightBold,
      color: colors.red[800],
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  }),
  { name: "change-admin-password-styles", flip: null }
);

export interface ChangePasswordProps {}

export const ChangePassword = (props: ChangePasswordProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [showPassword, setShowPassword] = useState(false);

  const resetForm = () => {
    formik.resetForm();
  };

  // Handle formik

  const initFormValues = () => {
    return {
      current_password: "",
      password: "",
      confirm_password: "",
    };
  };

  const formValidation = () => {
    return {
      current_password: Yup.string()
        .min(6, intl.formatMessage({ id: "error.tooShort" }))
        .required(intl.formatMessage({ id: "error.enterValidPassword" })),
      password: Yup.string()
        .min(6, intl.formatMessage({ id: "error.tooShort" }))
        .required(intl.formatMessage({ id: "error.enterValidPassword" })),
      confirm_password: Yup.string()
        .min(6, intl.formatMessage({ id: "error.tooShort" }))
        .required(intl.formatMessage({ id: "error.enterValidPassword" })),
    };
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({ ...formValidation() }),
    onSubmit: async (
      values: any,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      let formData = { ...values };

      try {
        const response = await ICIPAdminService.changeAdminPassword(formData);
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              resetForm();
              break;
            case 150:
              // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            default:
              setErrors({ error: response.data.message });
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (error) {
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.error) {
      return formik.errors.error as string;
    }

    return "";
  };

  return (
    <>
      <ExpandedContent
        title={intl.formatMessage({ id: "sharedScreen.security" })}
      >
        <Grid container>
          <Grid item sm={3}>
            <Box component="div">
              <SettingsItem
                title={intl.formatMessage({ id: "sharedScreen.password" })}
                subTitle={intl.formatMessage({
                  id: "sharedScreen.resetPasswordNotice",
                })}
              />
            </Box>
          </Grid>
          <Grid item sm={4}>
            <Box component="div" className={classes.wrapper}>
              <Box component="div" mb={2}>
                <Typography variant="h4" component="h4">
                  <FormattedMessage id="sharedScreen.changePassword" />
                </Typography>
                <Box mt={2} component="div" className={classes.error}>
                  {genericError()}
                </Box>

                <form
                  noValidate
                  autoComplete="off"
                  onSubmit={formik.handleSubmit}
                >
                  <Box component="div">
                    <InputField
                      fullWidth
                      id="current_password"
                      name="current_password"
                      label=""
                      placeholder={intl.formatMessage({
                        id: "sharedScreen.currentPassword",
                      })}
                      type={showPassword ? "text" : "password"}
                      value={formik.values.current_password}
                      onChange={formik.handleChange}
                      error={
                        Boolean(formik.touched.current_password) &&
                        Boolean(formik.errors.current_password)
                      }
                      helperText={
                        formik.touched.current_password &&
                        formik.errors.current_password
                      }
                    />
                  </Box>
                  <Grid container component="div" spacing={1}>
                    <Grid item sm={6}>
                      <InputField
                        fullWidth
                        id="password"
                        name="password"
                        label=""
                        placeholder={intl.formatMessage({
                          id: "sharedScreen.password",
                        })}
                        type={showPassword ? "text" : "password"}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={
                          Boolean(formik.touched.password) &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password && formik.errors.password
                        }
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <InputField
                        fullWidth
                        id="confirm_password"
                        name="confirm_password"
                        label=""
                        placeholder={intl.formatMessage({
                          id: "sharedScreen.enterPasswordToConfirm",
                        })}
                        type={showPassword ? "text" : "password"}
                        value={formik.values.confirm_password}
                        onChange={formik.handleChange}
                        error={
                          Boolean(formik.touched.confirm_password) &&
                          Boolean(formik.errors.confirm_password)
                        }
                        helperText={
                          formik.touched.confirm_password &&
                          formik.errors.confirm_password
                        }
                      />
                    </Grid>
                  </Grid>
                  <Typography
                      component="p"
                      className={classes.showPasswordLink}
                      onClick={(_) => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                        <FormattedMessage id="sharedScreen.hidePassword" />
                    ) : (
                        <FormattedMessage id="sharedScreen.showPassword" />
                    )}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    mt={4}
                  >
                    <Box mr={2} component="div">
                      <InputButton
                        color="secondary"
                        fullWidth
                        size="medium"
                        variant="contained"
                        type="submit"
                        disabled={formik.isSubmitting}
                      >
                        <FormattedMessage id="sharedScreen.continue" />
                      </InputButton>
                    </Box>
                    {formik.isSubmitting ? (
                      <Box display="inline-block" className={classes.loader}>
                        <CircularProgress
                          color="secondary"
                          size={20}
                          thickness={4}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center">
                        <OutlineButton
                          disabled={formik.isSubmitting}
                          onClick={(_) => resetForm()}
                        >
                          <FormattedMessage id="sharedScreen.cancel" />
                        </OutlineButton>
                      </Box>
                    )}
                  </Box>
                </form>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ExpandedContent>
    </>
  );
};
